import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, Shield, Download, Clock, Sparkles, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Komponent licznika czasu pozostałego
const FreeAccessCountdown = ({ expiresAt }) => {
  const [timeLeft, setTimeLeft] = useState('');
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;

      if (diff <= 0) {
        setIsExpired(true);
        setTimeLeft('Wygasło');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setTimeLeft(`${hours}h ${minutes}min`);
      } else {
        setTimeLeft(`${minutes}min ${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  if (isExpired) return null;

  return (
    <Card 
      className="mb-6 border-0 overflow-hidden"
      style={{ 
        background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.2), rgba(16, 185, 129, 0.2))',
        border: '1px solid rgba(14, 165, 233, 0.3)'
      }}
    >
      <CardContent className="py-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'linear-gradient(135deg, #0ea5e9, #10b981)',
              boxShadow: '0 4px 15px rgba(14, 165, 233, 0.3)'
            }}
          >
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm text-slate-300">Darmowy dostęp PRO</p>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-sky-400" />
              <p className="text-lg font-bold text-white">{timeLeft}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export const ProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/welcome');
  };

  // Check if user has active free access
  const hasActiveFreeAccess = () => {
    if (!user?.free_access_until) return false;
    const expiry = new Date(user.free_access_until);
    return expiry > new Date();
  };

  return (
    <div className="min-h-screen" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 140px)', background: 'transparent' }} data-testid="profile-page">
      <div className="px-6 pt-8 pb-6">
        <button 
          onClick={() => navigate('/today')} 
          className="mb-4 text-slate-400 hover:text-white transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-2xl font-bold text-white mb-6">Profil</h1>

        {/* Free Access Countdown */}
        {hasActiveFreeAccess() && (
          <FreeAccessCountdown expiresAt={user.free_access_until} />
        )}

        {/* Profile Card */}
        <Card className="card-premium mb-6 border-0" style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(12px)' }}>
          <CardContent className="py-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div>
                <p className="font-semibold text-white">{user?.email}</p>
                <p className={`text-sm ${user?.subscription_status === 'active' || hasActiveFreeAccess() ? 'text-emerald-400' : 'text-slate-400'}`}>
                  {user?.subscription_status === 'active' 
                    ? `Plan ${user?.subscription_plan === 'yearly' ? 'roczny' : 'miesięczny'}`
                    : hasActiveFreeAccess()
                    ? 'Dostęp PRO (24h)'
                    : 'Plan darmowy'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Section */}
        <h2 className="font-semibold text-white mb-3">Statystyki</h2>
        <div className="grid grid-cols-3 gap-3 mb-6">
          <Card className="card-premium border-0 rounded-2xl" style={{ background: 'rgba(14,165,233,0.08)', border: '1px solid rgba(14,165,233,0.12)', backdropFilter: 'blur(8px)' }}>
            <CardContent className="py-4 text-center">
              <p className="text-2xl font-bold text-sky-400">{user?.completed_days?.length || 0}</p>
              <p className="text-xs text-slate-400">Ukończonych</p>
            </CardContent>
          </Card>
          <Card className="card-premium border-0 rounded-2xl" style={{ background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.12)', backdropFilter: 'blur(8px)' }}>
            <CardContent className="py-4 text-center">
              <p className="text-2xl font-bold text-orange-400">{user?.current_streak || 0}</p>
              <p className="text-xs text-slate-400">Obecna seria</p>
            </CardContent>
          </Card>
          <Card className="card-premium border-0 rounded-2xl" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.12)', backdropFilter: 'blur(8px)' }}>
            <CardContent className="py-4 text-center">
              <p className="text-2xl font-bold text-emerald-400">{user?.longest_streak || 0}</p>
              <p className="text-xs text-slate-400">Najdłuższa</p>
            </CardContent>
          </Card>
        </div>

        {/* Upgrade CTA */}
        {user?.subscription_status !== 'active' && !hasActiveFreeAccess() && (
          <div
            className="mb-6 rounded-xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(14,165,233,0.14) 0%, rgba(16,185,129,0.08) 100%)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(14,165,233,0.18)',
              boxShadow: '0 4px 32px rgba(14,165,233,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
            data-testid="upgrade-cta"
          >
            <div className="px-5 py-6">
              <div className="flex items-center gap-2.5 mb-3">
                <Sparkles className="w-5 h-5 text-sky-400" />
                <h3 className="text-base font-bold text-white">Odblokuj pełny dostęp</h3>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed mb-5">
                Kontynuuj naukę bez ograniczeń — wszystkie lekcje, Mentor, szkolenia specjalistyczne i biblioteka promptów.
              </p>
              <button
                onClick={() => navigate('/paywall')}
                className="w-full py-3 rounded-xl text-sm font-bold text-white transition-all"
                style={{
                  background: 'linear-gradient(135deg, #0ea5e9, #06b6d4)',
                  boxShadow: '0 4px 16px rgba(14,165,233,0.35)',
                }}
              >
                Sprawdź plany Premium
              </button>
            </div>
          </div>
        )}

        {/* Admin Button */}
        {user?.is_admin && (
          <Button 
            onClick={() => navigate('/admin')} 
            variant="outline" 
            className="w-full mb-3 h-12 rounded-xl border-slate-600 text-slate-300 hover:bg-white/10"
          >
            <Shield className="w-5 h-5 mr-2" /> Panel administratora
          </Button>
        )}

        {/* Menu Links */}
        <div className="space-y-1.5 mb-6 rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <Button onClick={() => navigate('/download')} variant="ghost" className="w-full justify-start h-12 text-slate-300 hover:text-white hover:bg-white/[0.06] rounded-none border-b border-white/[0.04]">
            <Download className="w-5 h-5 mr-3 text-slate-500" /> Pobierz aplikację
          </Button>
          <Button onClick={() => navigate('/legal/terms')} variant="ghost" className="w-full justify-start h-12 text-slate-300 hover:text-white hover:bg-white/[0.06] rounded-none border-b border-white/[0.04]">
            <Shield className="w-5 h-5 mr-3 text-slate-500" /> Regulamin
          </Button>
          <Button onClick={() => navigate('/legal/privacy')} variant="ghost" className="w-full justify-start h-12 text-slate-300 hover:text-white hover:bg-white/[0.06] rounded-none">
            <Shield className="w-5 h-5 mr-3 text-slate-500" /> Polityka prywatności
          </Button>
        </div>

        {/* Logout Button */}
        <Button 
          onClick={handleLogout} 
          variant="outline" 
          className="w-full h-12 rounded-xl text-red-400 border-red-400/30 hover:bg-red-500/10 hover:border-red-400"
        >
          <LogOut className="w-5 h-5 mr-2" /> Wyloguj się
        </Button>
      </div>
    </div>
  );
};
